import { Button } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import {
  AxiosResult,
  AxiosResultDefaultError,
} from '../../../../../api/request';
import Loading from '../../../../../components/info/Loading';
import {
  Partnership,
  CreatePartnershipData,
  createPartnershipRequest,
  updatePartnershipRequest,
  getPartnershipByIdRequest,
  getPeriodsForPartnershipRequest,
  getStudyPlanVersionOffersRequest,
  getBenefitTypesRequest,
  PeriodItem,
  StudyPlanVersionOfferItem,
  EbTypeItem,
} from '../../api/partnership';

import { checkPercentageAsNumber } from '../../../../../utils/currency';
import PartnershipDataSection from './sections/PartnershipDataSection';
import PartnershipDiscountsSection from './sections/PartnershipDiscountsSection';

const prefix = 'tariffAndTuition.partnerships';

interface PartnershipFormProps {
  recordToEdit: Partnership | null;
  onSubmit: (result: AxiosResult<Partnership, AxiosResultDefaultError>) => void;
  onCancel: () => void;
}

interface DiscountKey {
  periodId: number;
  studyPlanVersionOfferId: number;
}

interface FormValues {
  name: string;
  partnershipType: string;
  description: string;
  applyAutomatically: boolean;
  discounts: { [key: string]: string };
}

const parseDiscountKey = (key: string): DiscountKey => {
  const [periodId, studyPlanVersionOfferId] = key.split('-').map(Number);
  return { periodId, studyPlanVersionOfferId };
};

const makeDiscountKey = (periodId: number, offerId: number) =>
  `${periodId}-${offerId}`;

export const PartnershipForm = ({
  recordToEdit,
  onSubmit: onSubmitProp,
  onCancel,
}: PartnershipFormProps) => {
  const { t } = useTranslation();
  const editing = !!recordToEdit;

  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState<PeriodItem[]>([]);
  const [offers, setOffers] = useState<StudyPlanVersionOfferItem[]>([]);
  const [benefitTypes, setBenefitTypes] = useState<EbTypeItem[]>([]);
  const [yearFilter, setYearFilter] = useState('last5');
  const [loadedDiscounts, setLoadedDiscounts] = useState<{
    [key: string]: string;
  }>({});

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      partnershipType: '',
      description: '',
      applyAutomatically: false,
      discounts: {},
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = methods;

  const loadFormData = useCallback(async () => {
    const [periodsRes, offersRes, typesRes] = await Promise.all([
      getPeriodsForPartnershipRequest(yearFilter),
      getStudyPlanVersionOffersRequest(),
      getBenefitTypesRequest('simple'),
    ]);

    if (periodsRes.data) {
      setPeriods(periodsRes.data);
    }

    if (offersRes.data) {
      setOffers(offersRes.data);
    }

    if (typesRes.data) {
      setBenefitTypes(typesRes.data);
    }
  }, [yearFilter]);

  const loadPartnershipData = useCallback(async () => {
    if (recordToEdit) {
      const { data } = await getPartnershipByIdRequest(recordToEdit.id);
      if (data) {
        setValue('name', data.name);
        // Select expects { value, label } object to match options
        const matchingType = benefitTypes.find(
          (bt) => bt.id === data.partnershipType,
        );
        if (matchingType) {
          setValue('partnershipType', {
            value: matchingType.id,
            label: matchingType.name,
          } as any);
        }
        setValue('description', data.description || '');
        setValue('applyAutomatically', data.applyAutomatically ?? false);

        const discountsMap: { [key: string]: string } = {};
        data.discounts?.forEach((d) => {
          const key = makeDiscountKey(d.periodId, d.studyPlanVersionOfferId);
          discountsMap[key] = String(d.discountPercentage);
        });
        setLoadedDiscounts(discountsMap);
      }
    }
    setLoading(false);
  }, [recordToEdit, setValue, benefitTypes]);

  useEffect(() => {
    loadFormData();
  }, [loadFormData]);

  useEffect(() => {
    loadPartnershipData();
  }, [loadPartnershipData]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const discounts = Object.entries(values.discounts || {})
        .filter(
          ([, percentage]) =>
            percentage && checkPercentageAsNumber(percentage) > 0,
        )
        .map(([key, percentage]) => ({
          ...parseDiscountKey(key),
          discountPercentage: checkPercentageAsNumber(percentage),
        }));

      const payload: CreatePartnershipData = {
        name: values.name,
        partnershipType:
          (values.partnershipType as any)?.value ||
          values.partnershipType ||
          undefined,
        description: values.description || undefined,
        applyAutomatically: values.applyAutomatically,
        discounts,
      };

      const result = editing
        ? await updatePartnershipRequest(recordToEdit!.id, payload)
        : await createPartnershipRequest(payload);

      onSubmitProp(result);
    },
    [editing, recordToEdit, onSubmitProp],
  );

  if (loading) {
    return <Loading insideCard />;
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)} className="p-4">
        {/* Header with back button */}
        <Row className="mb-4">
          <Col>
            <Button
              type="button"
              text={t(`${prefix}.backToList`)}
              outlined
              icon="back"
              onClick={onCancel}
              size="sm"
              className="rounded-pill"
            />
          </Col>
        </Row>

        {/* Description text */}
        <Row className="mb-4">
          <Col xs="12">
            <p className="text-muted mb-0">{t(`${prefix}.formDescription`)}</p>
          </Col>
        </Row>

        <PartnershipDataSection benefitTypes={benefitTypes} />

        <PartnershipDiscountsSection
          periods={periods}
          offers={offers}
          loadedDiscounts={loadedDiscounts}
          yearFilter={yearFilter}
          onYearFilterChange={setYearFilter}
        />

        {/* Action buttons */}
        <Row className="mt-4">
          <Col xs="12" className="d-flex justify-content-end">
            <Button
              type="button"
              text={t('common.actions.cancel')}
              outlined
              onClick={onCancel}
              className="mr-3"
            />
            <Button
              type="submit"
              text={t('common.actions.saveChanges')}
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};
